import { z } from "zod";

export const splitterPageSpanSchema = z.object({
  /** One-indexed inclusive page number. */
  start: z.number().int().min(1),
  /** One-indexed inclusive page number. */
  end: z.number().int().min(1),
});

export const pageRangeSplitterSectionSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  destinationTableId: z.string().min(1),
  pageSpans: z.array(splitterPageSpanSchema).min(1),
});

export const pageRangeSplitterConfigSchema = z.object({
  mode: z.literal("page_range"),
  overlapPolicy: z.enum(["exclusive", "allowed"]),
  unassignedPolicy: z.enum(["review", "ignore", "fallback"]),
  fallbackSectionKey: z.string().optional(),
  maxSegments: z.number().int().min(1).max(100),
  sections: z.array(pageRangeSplitterSectionSchema).min(1).max(100),
});

export const logicalSplitterSectionTypeSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
});

export const logicalSplitterConfigSchema = z.object({
  mode: z.literal("logical"),
  sourceInboxId: z.string().min(1).nullable(),
  instructions: z.string().trim().min(10).max(4_000),
  sectionTypes: z.array(logicalSplitterSectionTypeSchema).max(50),
  routingMode: z.literal("project_rules"),
  reviewUncertainBoundaries: z.boolean(),
  reviewUncertainDestinations: z.boolean(),
  boundaryConfidenceThreshold: z.number().int().min(0).max(100).default(75),
  maxSegments: z.number().int().min(1).max(100).default(50),
});

export const splitterConfigSchema = z.discriminatedUnion("mode", [
  pageRangeSplitterConfigSchema,
  logicalSplitterConfigSchema,
]);

export type SplitterMode = z.infer<typeof splitterConfigSchema>["mode"];
export type SplitterOverlapPolicy = z.infer<
  typeof pageRangeSplitterConfigSchema
>["overlapPolicy"];
export type SplitterUnassignedPolicy = z.infer<
  typeof pageRangeSplitterConfigSchema
>["unassignedPolicy"];
export type SplitterPageSpan = z.infer<typeof splitterPageSpanSchema>;
export type PageRangeSplitterSection = z.infer<
  typeof pageRangeSplitterSectionSchema
>;
export type PageRangeSplitterConfig = z.infer<
  typeof pageRangeSplitterConfigSchema
>;
export type LogicalSplitterSectionType = z.infer<
  typeof logicalSplitterSectionTypeSchema
>;
export type LogicalSplitterConfig = z.infer<
  typeof logicalSplitterConfigSchema
>;
export type SplitterConfig = z.infer<typeof splitterConfigSchema>;

export const logicalSplitterModelSegmentSchema = z.object({
  sectionTypeKey: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  sectionTypeName: z.string().trim().min(1).max(120).optional(),
  startPage: z.number().int().min(1),
  endPage: z.number().int().min(1),
  confidence: z.number().int().min(0).max(100),
  reasoning: z.string().max(2_000),
});

export const logicalSplitterModelResultSchema = z.object({
  segments: z.array(logicalSplitterModelSegmentSchema).max(100),
});

export type LogicalSplitterModelSegment = z.infer<
  typeof logicalSplitterModelSegmentSchema
>;
export type LogicalSplitterModelResult = z.infer<
  typeof logicalSplitterModelResultSchema
>;

export const compiledLogicalSplitterSegmentSchema =
  logicalSplitterModelSegmentSchema.extend({
    segmentKey: z.string(),
    sectionTypeName: z.string(),
    pageCount: z.number().int().min(1),
    ordinal: z.number().int().min(0),
    boundaryReviewRequired: z.boolean(),
  });

export const compiledLogicalSplitterResultSchema = z.object({
  pageCount: z.number().int().min(1),
  segments: z.array(compiledLogicalSplitterSegmentSchema),
  unassignedPageSpans: z.array(splitterPageSpanSchema),
  overlaps: z.array(
    z.object({ page: z.number().int().min(1), segmentKeys: z.array(z.string()) }),
  ),
  reviewRequired: z.boolean(),
});

export type CompiledLogicalSplitterSegment = z.infer<
  typeof compiledLogicalSplitterSegmentSchema
>;
export type CompiledLogicalSplitterResult = z.infer<
  typeof compiledLogicalSplitterResultSchema
>;

export const routedLogicalSplitterSegmentSchema =
  compiledLogicalSplitterSegmentSchema.extend({
    destinationTableId: z.string().min(1).nullable(),
    destinationTableName: z.string().min(1).nullable(),
    routingConfidence: z.number().int().min(0).max(100).nullable(),
    routingReviewRequired: z.boolean(),
    routingReason: z.string().nullable(),
    candidates: z.array(
      z.object({
        classifierId: z.string(),
        classifierName: z.string(),
        tableId: z.string(),
        tableName: z.string(),
        score: z.number().int().min(0).max(100),
        reasoning: z.string(),
      }),
    ),
  });

export const routedLogicalSplitterResultSchema = z.object({
  pageCount: z.number().int().min(1),
  segments: z.array(routedLogicalSplitterSegmentSchema),
  unassignedPageSpans: z.array(splitterPageSpanSchema),
  overlaps: z.array(
    z.object({ page: z.number().int().min(1), segmentKeys: z.array(z.string()) }),
  ),
  reviewRequired: z.boolean(),
});

export type RoutedLogicalSplitterResult = z.infer<
  typeof routedLogicalSplitterResultSchema
>;

export type CompiledSplitterSegment = {
  segmentKey: string;
  sectionKey: string;
  sectionName: string;
  destinationTableId: string;
  pageSpans: SplitterPageSpan[];
  pageCount: number;
  ordinal: number;
  reviewRequired: boolean;
};

export type SplitterPageConflict = {
  page: number;
  sectionKeys: string[];
};

export type CompiledPageRangeSplit = {
  pageCount: number;
  segments: CompiledSplitterSegment[];
  unassignedPageSpans: SplitterPageSpan[];
  overlaps: SplitterPageConflict[];
  reviewRequired: boolean;
  uniqueAssignedPageCount: number;
  downstreamPageCount: number;
};

export type SplitterValidationIssue = {
  code:
    | "INVALID_PAGE_COUNT"
    | "NO_SECTIONS"
    | "TOO_MANY_SEGMENTS"
    | "DUPLICATE_SECTION_KEY"
    | "INVALID_SECTION_KEY"
    | "EMPTY_SECTION"
    | "INVALID_PAGE_SPAN"
    | "PAGE_OUT_OF_RANGE"
    | "OVERLAPPING_PAGES"
    | "FALLBACK_SECTION_REQUIRED";
  message: string;
  sectionKey?: string;
  page?: number;
};

export class SplitterValidationError extends Error {
  readonly issues: SplitterValidationIssue[];

  constructor(issues: SplitterValidationIssue[]) {
    super(issues.map((issue) => issue.message).join("; "));
    this.name = "SplitterValidationError";
    this.issues = issues;
  }
}

function pageSpanLength(span: SplitterPageSpan): number {
  return span.end - span.start + 1;
}

/**
 * Sorts spans and merges overlapping or adjacent ranges. Inputs and outputs are
 * one-indexed and inclusive.
 */
export function normalizePageSpans(
  pageSpans: readonly SplitterPageSpan[],
): SplitterPageSpan[] {
  const sorted = pageSpans
    .map((span) => ({ start: span.start, end: span.end }))
    .sort((left, right) => left.start - right.start || left.end - right.end);
  const normalized: SplitterPageSpan[] = [];

  for (const span of sorted) {
    const previous = normalized[normalized.length - 1];
    if (!previous || span.start > previous.end + 1) {
      normalized.push(span);
      continue;
    }
    previous.end = Math.max(previous.end, span.end);
  }

  return normalized;
}

function pagesToSpans(pages: readonly number[]): SplitterPageSpan[] {
  if (pages.length === 0) return [];
  const spans: SplitterPageSpan[] = [];
  let start = pages[0];
  let end = pages[0];

  for (let index = 1; index < pages.length; index += 1) {
    const page = pages[index];
    if (page === end + 1) {
      end = page;
      continue;
    }
    spans.push({ start, end });
    start = page;
    end = page;
  }
  spans.push({ start, end });
  return spans;
}

function validateConfig(
  config: PageRangeSplitterConfig,
  pageCount: number,
): SplitterValidationIssue[] {
  const issues: SplitterValidationIssue[] = [];
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    issues.push({
      code: "INVALID_PAGE_COUNT",
      message: "pageCount must be a positive integer",
    });
  }
  if (config.sections.length === 0) {
    issues.push({ code: "NO_SECTIONS", message: "Add at least one section" });
  }
  if (config.sections.length > config.maxSegments) {
    issues.push({
      code: "TOO_MANY_SEGMENTS",
      message: `Splitter produces ${config.sections.length} segments, above its ${config.maxSegments} segment limit`,
    });
  }

  const sectionKeys = new Set<string>();
  for (const section of config.sections) {
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(section.key)) {
      issues.push({
        code: "INVALID_SECTION_KEY",
        message: `Section key ${section.key} must use lowercase letters, numbers, and underscores`,
        sectionKey: section.key,
      });
    }
    if (sectionKeys.has(section.key)) {
      issues.push({
        code: "DUPLICATE_SECTION_KEY",
        message: `Section key ${section.key} is duplicated`,
        sectionKey: section.key,
      });
    }
    sectionKeys.add(section.key);

    if (section.pageSpans.length === 0) {
      issues.push({
        code: "EMPTY_SECTION",
        message: `Section ${section.key} has no pages`,
        sectionKey: section.key,
      });
    }
    for (const span of section.pageSpans) {
      if (
        !Number.isInteger(span.start) ||
        !Number.isInteger(span.end) ||
        span.start < 1 ||
        span.end < span.start
      ) {
        issues.push({
          code: "INVALID_PAGE_SPAN",
          message: `Section ${section.key} has an invalid page span ${span.start}-${span.end}`,
          sectionKey: section.key,
        });
      } else if (span.end > pageCount) {
        issues.push({
          code: "PAGE_OUT_OF_RANGE",
          message: `Section ${section.key} ends at page ${span.end}, beyond document page ${pageCount}`,
          sectionKey: section.key,
          page: span.end,
        });
      }
    }
  }

  if (
    config.unassignedPolicy === "fallback" &&
    (!config.fallbackSectionKey || !sectionKeys.has(config.fallbackSectionKey))
  ) {
    issues.push({
      code: "FALLBACK_SECTION_REQUIRED",
      message: "fallbackSectionKey must reference a configured section",
      sectionKey: config.fallbackSectionKey,
    });
  }

  return issues;
}

export function compilePageRangeSplitter(
  config: PageRangeSplitterConfig,
  pageCount: number,
): CompiledPageRangeSplit {
  const issues = validateConfig(config, pageCount);
  if (issues.length > 0) throw new SplitterValidationError(issues);

  const pageAssignments = new Map<number, string[]>();
  const normalizedSections = config.sections.map((section) => ({
    ...section,
    pageSpans: normalizePageSpans(section.pageSpans),
  }));

  for (const section of normalizedSections) {
    for (const span of section.pageSpans) {
      for (let page = span.start; page <= span.end; page += 1) {
        const assignments = pageAssignments.get(page) ?? [];
        assignments.push(section.key);
        pageAssignments.set(page, assignments);
      }
    }
  }

  const overlaps = Array.from(pageAssignments.entries())
    .filter(([, sectionKeys]) => sectionKeys.length > 1)
    .map(([page, sectionKeys]) => ({ page, sectionKeys: [...sectionKeys] }));
  if (config.overlapPolicy === "exclusive" && overlaps.length > 0) {
    throw new SplitterValidationError(
      overlaps.map(({ page, sectionKeys }) => ({
        code: "OVERLAPPING_PAGES",
        message: `Page ${page} is assigned to multiple sections: ${sectionKeys.join(", ")}`,
        page,
      })),
    );
  }

  const unassignedPages: number[] = [];
  for (let page = 1; page <= pageCount; page += 1) {
    if (!pageAssignments.has(page)) unassignedPages.push(page);
  }
  const originalUnassignedPageSpans = pagesToSpans(unassignedPages);

  if (
    config.unassignedPolicy === "fallback" &&
    originalUnassignedPageSpans.length > 0
  ) {
    const fallback = normalizedSections.find(
      (section) => section.key === config.fallbackSectionKey,
    );
    if (!fallback) {
      throw new SplitterValidationError([
        {
          code: "FALLBACK_SECTION_REQUIRED",
          message: "Configured fallback section was not found",
        },
      ]);
    }
    fallback.pageSpans = normalizePageSpans([
      ...fallback.pageSpans,
      ...originalUnassignedPageSpans,
    ]);
    for (const page of unassignedPages) pageAssignments.set(page, [fallback.key]);
  }

  const reviewRequired =
    config.unassignedPolicy === "review" && unassignedPages.length > 0;
  const segments = normalizedSections.map(
    (section, ordinal): CompiledSplitterSegment => ({
      segmentKey: `${section.key}:1`,
      sectionKey: section.key,
      sectionName: section.name,
      destinationTableId: section.destinationTableId,
      pageSpans: section.pageSpans,
      pageCount: section.pageSpans.reduce(
        (total, span) => total + pageSpanLength(span),
        0,
      ),
      ordinal,
      reviewRequired: false,
    }),
  );

  return {
    pageCount,
    segments,
    unassignedPageSpans:
      config.unassignedPolicy === "fallback"
        ? []
        : originalUnassignedPageSpans,
    overlaps,
    reviewRequired,
    uniqueAssignedPageCount: pageAssignments.size,
    downstreamPageCount: segments.reduce(
      (total, segment) => total + segment.pageCount,
      0,
    ),
  };
}

export function compileLogicalSplitterResult(
  configInput: LogicalSplitterConfig,
  pageCount: number,
  resultInput: LogicalSplitterModelResult,
): CompiledLogicalSplitterResult {
  const config = logicalSplitterConfigSchema.parse(configInput);
  const result = logicalSplitterModelResultSchema.parse(resultInput);
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    throw new SplitterValidationError([
      { code: "INVALID_PAGE_COUNT", message: "pageCount must be a positive integer" },
    ]);
  }
  if (result.segments.length > config.maxSegments) {
    throw new SplitterValidationError([
      {
        code: "TOO_MANY_SEGMENTS",
        message: `Splitter produced ${result.segments.length} segments, above its ${config.maxSegments} segment limit`,
      },
    ]);
  }

  const typeNames = new Map(
    config.sectionTypes.map((sectionType) => [sectionType.key, sectionType.name]),
  );
  const discoveryMode = config.sectionTypes.length === 0;
  const assignments = new Map<number, string[]>();
  const issues: SplitterValidationIssue[] = [];
  const segments = result.segments.map((segment, ordinal) => {
    const sectionTypeName = typeNames.get(segment.sectionTypeKey);
    if (!discoveryMode && !sectionTypeName) {
      issues.push({
        code: "INVALID_SECTION_KEY",
        message: `Unknown logical section type ${segment.sectionTypeKey}`,
        sectionKey: segment.sectionTypeKey,
      });
    }
    if (segment.endPage < segment.startPage || segment.endPage > pageCount) {
      issues.push({
        code: segment.endPage > pageCount ? "PAGE_OUT_OF_RANGE" : "INVALID_PAGE_SPAN",
        message: `Logical section ${ordinal + 1} has invalid pages ${segment.startPage}-${segment.endPage}`,
        sectionKey: segment.sectionTypeKey,
        page: segment.endPage,
      });
    }
    const segmentKey = `${segment.sectionTypeKey}:${ordinal + 1}`;
    for (
      let page = segment.startPage;
      page <= Math.min(segment.endPage, pageCount);
      page += 1
    ) {
      assignments.set(page, [...(assignments.get(page) ?? []), segmentKey]);
    }
    return {
      ...segment,
      segmentKey,
      sectionTypeName:
        sectionTypeName ??
        segment.sectionTypeName ??
        segment.sectionTypeKey
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      pageCount: Math.max(0, segment.endPage - segment.startPage + 1),
      ordinal,
      boundaryReviewRequired:
        segment.confidence < config.boundaryConfidenceThreshold,
    };
  });
  if (issues.length) throw new SplitterValidationError(issues);

  const unassignedPages = Array.from({ length: pageCount }, (_, index) => index + 1)
    .filter((page) => !assignments.has(page));
  const overlaps = Array.from(assignments.entries())
    .filter(([, segmentKeys]) => segmentKeys.length > 1)
    .map(([page, segmentKeys]) => ({ page, segmentKeys }));
  const unassignedPageSpans = pagesToSpans(unassignedPages);
  const boundaryIssue =
    unassignedPageSpans.length > 0 ||
    overlaps.length > 0 ||
    segments.some((segment) => segment.boundaryReviewRequired);

  return compiledLogicalSplitterResultSchema.parse({
    pageCount,
    segments,
    unassignedPageSpans,
    overlaps,
    reviewRequired: config.reviewUncertainBoundaries && boundaryIssue,
  });
}
